import React, { useState } from 'react';
import {
  Container, Paper, TextField, Button, Typography, Box, List,
  ListItemButton, ListItemText, CircularProgress, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Chip, IconButton, ListItem
} from '@mui/material';
import { 
  Search, Add, Person, Phone as PhoneIcon, Email as EmailIcon, Air, Edit
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { customerAPI } from '../../services/api';
import AppHeader from '../../components/AppHeader';

const capitalizeWords = (text) => {
  if (!text) return '';
  return text.split(/(\s+|-)/g).map((part) => {
    if (part === ' ' || part === '-') return part;
    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
  }).join('');
};

const formatPhoneNumber = (text) => {
  if (!text) return '';
  let cleaned = text.trim().replace(/[^\d+]/g, '');
  if (cleaned.startsWith('0')) {
    cleaned = '+49' + cleaned.substring(1);
  } else if (cleaned && !cleaned.startsWith('+')) {
    cleaned = '+49' + cleaned;
  }
  return cleaned;
};

const validateKundennummer = (value) => {
  const cleaned = value.replace(/\D/g, '');
  return cleaned.length === 5;
};

const formatKundennummer = (value) => {
  return value.replace(/\D/g, '').slice(0, 5);
};

export default function WPCustomerSearchPage() {
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewCustomerDialog, setShowNewCustomerDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    ku_nr: '',
    name: '',
    strasse: '',
    plz: '',
    ort: '',
    email: '',
    mobilfunk: '',
    festnetz: ''
  });
  const [newCustomerErrors, setNewCustomerErrors] = useState({});
  
  // Bearbeiten Dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [editErrors, setEditErrors] = useState({});

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Bitte Suchbegriff eingeben');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await customerAPI.search(searchQuery);
      const results = response.customers || [];
      setCustomers(results);
      
      if (results.length === 0) {
        setError('Keine Kunden gefunden');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    navigate('/protocol/wp-form', { state: { customer } });
  };

  const handleNewCustomer = () => {
    setShowNewCustomerDialog(true);
    setNewCustomer({ 
      ku_nr: '',
      name: '',
      strasse: '',
      plz: '',
      ort: '',
      email: '',
      mobilfunk: '',
      festnetz: ''
    });
    setNewCustomerErrors({});
  };

  const validateNewCustomer = () => {
    const errors = {};
    if (!newCustomer.name.trim()) errors.name = 'Name ist erforderlich';
    if (!newCustomer.ku_nr.trim()) {
      errors.ku_nr = 'Kundennummer ist erforderlich';
    } else if (!validateKundennummer(newCustomer.ku_nr)) {
      errors.ku_nr = 'Kundennummer muss 5-stellig sein';
    }
    if (newCustomer.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCustomer.email)) {
      errors.email = 'Ungültige E-Mail-Adresse';
    }
    setNewCustomerErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveNewCustomer = async () => {
    if (!validateNewCustomer()) return;

    setLoading(true);
    try {
      const customerData = {
        ...newCustomer,
        mobilfunk: newCustomer.mobilfunk ? formatPhoneNumber(newCustomer.mobilfunk) : '',
        festnetz: newCustomer.festnetz ? formatPhoneNumber(newCustomer.festnetz) : ''
      };
      const savedCustomer = await customerAPI.create(customerData);
      setShowNewCustomerDialog(false);
      navigate('/protocol/wp-form', { state: { customer: savedCustomer } });
    } catch (err) {
      console.error('Error creating customer:', err);
      setError('Fehler beim Erstellen des Kunden');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (customer) => {
    setEditCustomer({...customer});
    setEditErrors({});
    setEditDialog(true);
  };

  const handleEditChange = (field, value) => {
    let processedValue = value;
    
    if (field === 'name' || field === 'strasse' || field === 'ort') {
      processedValue = capitalizeWords(value);
    } else if (field === 'mobilfunk' || field === 'festnetz') {
      processedValue = formatPhoneNumber(value);
    }
    
    setEditCustomer(prev => ({ ...prev, [field]: processedValue }));
    
    if (editErrors[field]) {
      setEditErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateEdit = () => {
    const errors = {};
    
    if (!editCustomer.name || editCustomer.name.trim() === '') {
      errors.name = 'Name ist erforderlich';
    }
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveEdit = async () => {
    if (!validateEdit()) {
      return;
    }

    try {
      const updatedData = {
        ...editCustomer,
        mobilfunk: editCustomer.mobilfunk ? formatPhoneNumber(editCustomer.mobilfunk) : '',
        festnetz: editCustomer.festnetz ? formatPhoneNumber(editCustomer.festnetz) : ''
      };
      
      await customerAPI.update(updatedData);
      setEditDialog(false);
      
      // Update local customer list
      setCustomers(customers.map(c => 
        c.ku_nr === editCustomer.ku_nr ? updatedData : c
      ));
    } catch (err) {
      console.error('Update customer error:', err);
      setError('Fehler beim Aktualisieren des Kunden');
    }
  };

  return (
    <>
      <AppHeader title="Kundensuche - Wärmepumpe" />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#4caf50', fontWeight: 'bold' }}>
            <Air sx={{ mr: 1, verticalAlign: 'middle' }} />
            Kundensuche für Wärmepumpen-Wartung
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Name, Kundennummer oder Adresse..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} /> }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{ minWidth: 120, bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
            >
              {loading ? <CircularProgress size={24} /> : 'Suchen'}
            </Button>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<Add />}
            onClick={handleNewCustomer}
            sx={{ mb: 3, borderColor: '#4caf50', color: '#4caf50' }}
          >
            Neuer Kunde anlegen
          </Button>

          {customers.length > 0 && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>{customers.length} Kunde(n) gefunden</Typography>
              <List>
                {customers.map((customer) => (
                  <ListItem 
                    key={customer.ku_nr}
                    disablePadding
                    sx={{ 
                      mb: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      bgcolor: '#fafafa'
                    }}
                  >
                    <ListItemButton onClick={() => handleSelectCustomer(customer)}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                        <Person sx={{ mr: 2, color: '#4caf50', mt: 0.5 }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6">
                            {customer.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {customer.ku_nr && `Kd.-Nr.: ${customer.ku_nr}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {customer.strasse && `${customer.strasse}, `}
                            {customer.plz && `${customer.plz} `}
                            {customer.ort}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {customer.email && (
                              <Chip
                                icon={<EmailIcon />}
                                label={customer.email}
                                size="small"
                                clickable
                                color="primary"
                                variant="outlined"
                              />
                            )}
                            {customer.mobilfunk && (
                              <Chip
                                icon={<PhoneIcon />}
                                label={`Mobil: ${customer.mobilfunk}`}
                                size="small"
                                clickable
                                color="primary"
                                variant="outlined"
                              />
                            )}
                            {customer.festnetz && (
                              <Chip
                                icon={<PhoneIcon />}
                                label={`Festnetz: ${customer.festnetz}`}
                                size="small"
                                clickable
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEdit(customer);
                          }}
                          sx={{ ml: 1 }}
                        >
                          <Edit />
                        </IconButton>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Paper>
      </Container>

      <Dialog open={showNewCustomerDialog} onClose={() => setShowNewCustomerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4caf50', color: 'white' }}>Neuen Kunden anlegen</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Kundennummer *"
              value={newCustomer.ku_nr}
              onChange={(e) => setNewCustomer({ ...newCustomer, ku_nr: formatKundennummer(e.target.value) })}
              error={!!newCustomerErrors.ku_nr}
              helperText={newCustomerErrors.ku_nr || 'Genau 5 Ziffern erforderlich'}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 5 }}
            />
            <TextField
              fullWidth
              label="Name *"
              value={newCustomer.name}
              onChange={(e) => setNewCustomer({ ...newCustomer, name: capitalizeWords(e.target.value) })}
              error={!!newCustomerErrors.name}
              helperText={newCustomerErrors.name}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="E-Mail"
              type="email"
              value={newCustomer.email}
              onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value.toLowerCase() })}
              error={!!newCustomerErrors.email}
              helperText={newCustomerErrors.email}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Mobilfunk"
              value={newCustomer.mobilfunk}
              onChange={(e) => setNewCustomer({ ...newCustomer, mobilfunk: formatPhoneNumber(e.target.value) })}
              helperText="Führende 0 wird durch +49 ersetzt"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Festnetz"
              value={newCustomer.festnetz}
              onChange={(e) => setNewCustomer({ ...newCustomer, festnetz: formatPhoneNumber(e.target.value) })}
              helperText="Führende 0 wird durch +49 ersetzt"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Straße"
              value={newCustomer.strasse}
              onChange={(e) => setNewCustomer({ ...newCustomer, strasse: capitalizeWords(e.target.value) })}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="PLZ"
                  value={newCustomer.plz}
                  onChange={(e) => setNewCustomer({ ...newCustomer, plz: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                  inputProps={{ maxLength: 5 }}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Ort"
                  value={newCustomer.ort}
                  onChange={(e) => setNewCustomer({ ...newCustomer, ort: capitalizeWords(e.target.value) })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewCustomerDialog(false)}>ABBRECHEN</Button>
          <Button onClick={handleSaveNewCustomer} variant="contained" disabled={loading}
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
          >
            {loading ? <CircularProgress size={24} /> : 'KUNDE ANLEGEN'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bearbeiten Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4caf50', color: 'white' }}>Kunde bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Kundennummer"
              value={editCustomer?.ku_nr || ''}
              disabled
              sx={{ mb: 2, bgcolor: '#f5f5f5' }}
            />
            <TextField
              fullWidth
              label="Name *"
              value={editCustomer?.name || ''}
              onChange={(e) => handleEditChange('name', e.target.value)}
              error={!!editErrors.name}
              helperText={editErrors.name}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="E-Mail"
              type="email"
              value={editCustomer?.email || ''}
              onChange={(e) => handleEditChange('email', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Mobilfunk"
              value={editCustomer?.mobilfunk || ''}
              onChange={(e) => handleEditChange('mobilfunk', e.target.value)}
              helperText="Führende 0 wird durch +49 ersetzt"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Festnetz"
              value={editCustomer?.festnetz || ''}
              onChange={(e) => handleEditChange('festnetz', e.target.value)}
              helperText="Führende 0 wird durch +49 ersetzt"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Straße"
              value={editCustomer?.strasse || ''}
              onChange={(e) => handleEditChange('strasse', e.target.value)}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="PLZ"
                  value={editCustomer?.plz || ''}
                  onChange={(e) => handleEditChange('plz', e.target.value)}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Ort"
                  value={editCustomer?.ort || ''}
                  onChange={(e) => handleEditChange('ort', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>
            ABBRECHEN
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}>
            SPEICHERN
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
